import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  useToast,
  Flex,
  Avatar,
  Text,
  Checkbox,
  CheckboxGroup,
  Stack,
  Switch,
  Grid,
} from '@chakra-ui/react';
import { apiService } from '../services/apiService';

export default function MemberCreate() {
  const navigate = useNavigate();
  const toast = useToast();
  
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [groups, setGroups] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    adresse: '',
    ville: '',
    code_postal: '',
    date_adhesion: new Date().toISOString().split('T')[0],
    date_naissance: '',
    lieu_naissance: '',
    baptise: false,
    date_bapteme: '',
    eglise_bapteme: '',
    confirme: false,
    date_confirmation: '',
    eglise_confirmation: '',
    type_membre: '',
    group_ids: [],
    fonction_id: ''
  });

  // Charger les groupes et fonctions
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const response = await apiService.makeRequest('/members/meta/groups-and-functions');
        setGroups(response.groups || []);
        setFunctions(response.functions || []);
      } catch (err) {
        console.error('Error fetching metadata:', err);
      }
    };
    fetchMetadata();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    let processedValue = value;
    
    if (name === 'nom' || name === 'prenom') {
      processedValue = value.replace(/[^a-zA-ZÀ-ÿ\s\-']/g, '');
    }
    
    if (name === 'telephone') {
      processedValue = value.replace(/[^\d+\s]/g, '');
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Erreur',
          description: 'Veuillez sélectionner une image valide',
          status: 'error',
          duration: 3000,
        });
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Erreur',
          description: "L'image ne doit pas dépasser 5MB",
          status: 'error',
          duration: 3000,
        });
        return;
      }

      setPhotoFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

    const handleSubmit = async (e) => {
      e.preventDefault();
      console.log('=== DÉBUT handleSubmit ===');
      console.log('FormData avant nettoyage:', formData);

    try {
      setSaving(true);
      
      const formatPhoneNumber = (phone) => {
        if (!phone) return '';
        let cleaned = phone.replace(/[^\d+]/g, '');
        if (cleaned.startsWith('0')) {
          cleaned = '+33' + cleaned.substring(1);
        }
        if (!cleaned.startsWith('+')) {
          cleaned = '+33' + cleaned;
        }
        return cleaned;
      };
      
      const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
        const allowedFields = [
          'matricule', 'nom', 'prenom', 'email', 'telephone',
          'date_naissance', 'lieu_naissance', 'adresse', 'adresse_postale',
          'code_postal', 'ville', 'date_adhesion', 'numero_carte',
          'baptise', 'date_bapteme', 'eglise_bapteme',
          'confirme', 'date_confirmation', 'eglise_confirmation',
          'type_membre', 'group_ids', 'fonction_id'
        ];

        if (allowedFields.includes(key) && value !== null && value !== undefined) {
          if (key === 'group_ids') {
            acc[key] = value; // toujours inclure même si tableau vide
          } else if (key === 'telephone') {
            if (value !== '') acc[key] = formatPhoneNumber(value);
          } else if (key === 'baptise' || key === 'confirme') {
            acc[key] = value; // toujours inclure les booléens (même false)
          } else if (value !== '') {
            acc[key] = value;
          }
        }
        return acc;
      }, {});
      
      if (photoFile) {
        cleanedData.photo = photoFile;
      }
      
      console.log('=== Données nettoyées envoyées au backend ===');
      console.log('email:', cleanedData.email);
      console.log('date_adhesion:', cleanedData.date_adhesion);
      console.log('Tous les champs:', Object.keys(cleanedData));

      await apiService.createMember(cleanedData);
      
      toast({
        title: 'Succès',
        description: 'Membre créé avec succès',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      navigate('/members');
    } catch (err) {
      let errorMessage = err.message || 'Erreur lors de la création';
      
      if (err.errors && Array.isArray(err.errors)) {
        errorMessage = err.errors.map(e => `${e.field}: ${e.message}`).join('\n');
      } else if (err.data && err.data.errors && Array.isArray(err.data.errors)) {
        errorMessage = err.data.errors.map(e => `${e.field}: ${e.message}`).join('\n');
      }
      
      toast({
        title: 'Erreur de validation',
        description: errorMessage,
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box maxW="800px" mx="auto">
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Ajouter un nouveau membre</Heading>
        <Button variant="ghost" onClick={() => navigate('/members')}>
          Retour
        </Button>
      </Flex>

      <Box bg="white" p={6} borderRadius="md" shadow="sm">
        <Flex direction="column" align="center" mb={6}>
          <Avatar
            size="2xl"
            name={`${formData.prenom} ${formData.nom}`}
            src={photoPreview}
            mb={4}
          />
          <Input
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            display="none"
            id="photo-upload"
          />
          <Button
            as="label"
            htmlFor="photo-upload"
            size="sm"
            colorScheme="blue"
            variant="outline"
            cursor="pointer"
          >
            Ajouter une photo
          </Button>
          {photoFile && (
            <Text fontSize="sm" color="gray.600" mt={2}>
              Photo sélectionnée: {photoFile.name}
            </Text>
          )}
        </Flex>

        <form onSubmit={handleSubmit}>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" color="gray.500">
              Le matricule et le numéro de carte sont générés automatiquement et sont uniques.
            </Text>

            <HStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Nom</FormLabel>
                <Input
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  placeholder="Nom"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Prénom</FormLabel>
                <Input
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  placeholder="Prénom"
                />
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="email@example.com"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Téléphone</FormLabel>
              <Input
                name="telephone"
                value={formData.telephone}
                onChange={handleChange}
                placeholder="06 12 34 56 78 ou +33 6 12 34 56 78"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Adresse</FormLabel>
              <Input
                name="adresse"
                value={formData.adresse}
                onChange={handleChange}
                placeholder="Adresse"
              />
            </FormControl>

            <HStack spacing={4}>
              <FormControl>
                <FormLabel>Code postal</FormLabel>
                <Input
                  name="code_postal"
                  value={formData.code_postal}
                  onChange={handleChange}
                  placeholder="75000"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Ville</FormLabel>
                <Input
                  name="ville"
                  value={formData.ville}
                  onChange={handleChange}
                  placeholder="Paris"
                />
              </FormControl>
            </HStack>

            <HStack spacing={4}>
              <FormControl>
                <FormLabel>Date de naissance</FormLabel>
                <Input
                  type="date"
                  name="date_naissance"
                  value={formData.date_naissance}
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Lieu de naissance</FormLabel>
                <Input
                  name="lieu_naissance"
                  value={formData.lieu_naissance}
                  onChange={handleChange}
                  placeholder="Ville de naissance"
                />
              </FormControl>
            </HStack>

            <FormControl isRequired>
              <FormLabel>Date d'adhésion</FormLabel>
              <Input
                type="date"
                name="date_adhesion"
                value={formData.date_adhesion}
                onChange={handleChange}
              />
            </FormControl>

            {/* Informations religieuses */}
            <Text fontWeight="600" fontSize="sm" color="gray.600">Informations religieuses</Text>

            <FormControl display="flex" alignItems="center" w="auto">
              <FormLabel mb={0} minW="80px">Baptisé(e)</FormLabel>
              <Switch
                isChecked={formData.baptise}
                onChange={(e) => setFormData(prev => ({ ...prev, baptise: e.target.checked }))}
                colorScheme="green"
              />
            </FormControl>

            {formData.baptise && (
              <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr' }} gap={3}>
                <FormControl>
                  <FormLabel fontSize="sm">Date du baptême</FormLabel>
                  <Input
                    type="date"
                    name="date_bapteme"
                    value={formData.date_bapteme}
                    onChange={handleChange}
                    size="sm"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Église du baptême</FormLabel>
                  <Input
                    name="eglise_bapteme"
                    value={formData.eglise_bapteme}
                    onChange={handleChange}
                    placeholder="Nom de l'église"
                    size="sm"
                  />
                </FormControl>
              </Grid>
            )}

            <FormControl display="flex" alignItems="center" w="auto">
              <FormLabel mb={0} minW="80px">Confirmé(e)</FormLabel>
              <Switch
                isChecked={formData.confirme}
                onChange={(e) => setFormData(prev => ({ ...prev, confirme: e.target.checked }))}
                colorScheme="green"
              />
            </FormControl>

            {formData.confirme && (
              <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr' }} gap={3}>
                <FormControl>
                  <FormLabel fontSize="sm">Date de confirmation</FormLabel>
                  <Input
                    type="date"
                    name="date_confirmation"
                    value={formData.date_confirmation}
                    onChange={handleChange}
                    size="sm"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Église de confirmation</FormLabel>
                  <Input
                    name="eglise_confirmation"
                    value={formData.eglise_confirmation}
                    onChange={handleChange}
                    placeholder="Nom de l'église"
                    size="sm"
                  />
                </FormControl>
              </Grid>
            )}

            {/* Informations administratives */}
            <HStack spacing={4}>
              <FormControl>
                <FormLabel>Type de membre</FormLabel>
                <Select
                  name="type_membre"
                  value={formData.type_membre}
                  onChange={handleChange}
                  placeholder="Sélectionner un type"
                >
                  <option value="Communiant">Communiant</option>
                  <option value="Non communiant">Non communiant</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Groupe(s)</FormLabel>
                <CheckboxGroup
                  value={formData.group_ids.map(String)}
                  onChange={(vals) => setFormData(prev => ({ ...prev, group_ids: vals.map(Number) }))}
                >
                  <Stack spacing={2} mt={1}>
                    {groups.map((group) => (
                      <Checkbox key={group.id} value={String(group.id)}>
                        {group.nom}
                      </Checkbox>
                    ))}
                  </Stack>
                </CheckboxGroup>
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel>Fonction</FormLabel>
              <Select
                name="fonction_id"
                value={formData.fonction_id}
                onChange={handleChange}
                placeholder="Sélectionner une fonction"
              >
                {functions.map((func) => (
                  <option key={func.id} value={func.id}>
                    {func.nom}
                  </option>
                ))}
              </Select>
            </FormControl>

            <HStack spacing={4} pt={4}>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={saving}
                loadingText="Création..."
                flex={1}
              >
                Créer le membre
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/members')}
                isDisabled={saving}
              >
                Annuler
              </Button>
            </HStack>
          </VStack>
        </form>
      </Box>
    </Box>
  );
}